import { createRequest, getRequestFields } from 'API';
import {
  analyzeInvoice,
  getReimbursements,
  isCannabisRefundEntitled,
} from 'API/Reimbursements';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { HTTP_STATUS_CODES } from 'Enum/HttpStatusCodes';
import { REIMBURSEMENTS_FORM_ID } from 'Enum/Reimbursements';
import { useAppSelector, useDictionary, usePersonalDetails } from 'Hooks';
import { useGeneralOptions } from 'Hooks/useGeneralOptions';
import { useLoader } from 'Hooks/useLoader';
import { ICannabisFormData } from 'Modules/Reimbursements/Cannabis/CannabisMain';
import { CancelEditDialog } from 'Modules/Reimbursements/Cannabis/CannabisMain/CancelEditDialog';
import { DeleteDialog } from 'Modules/Reimbursements/Cannabis/CannabisMain/DeleteDialog';
import { maxInvoiceLength } from 'Modules/Reimbursements/Cannabis/CannabisMain/NewInvoiceForm/Form';
import { ReturnToMainDialog } from 'Modules/Reimbursements/Cannabis/CannabisMain/ReturnToMainDialog';
import { SuccessDialog } from 'Modules/Reimbursements/Cannabis/CannabisMain/SuccessDialog';
import { DuplicateFileDialog } from 'Modules/Reimbursements/Cannabis/DuplicateFileDialog';
import {
  checkExtension,
  checkFileSize,
} from 'Modules/UniversalRequest/FileUpload/Utils';
import { notLettersOrNumbersWithExtraChars } from 'Regex';
import { isFileValid } from 'Utils/isFileValid';
import { reimbursementsFormPrepareCannabisFormData } from 'Utils/reimbursements';
import { scrollToTop } from 'Utils/scrollToTop';
import { createContext, FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const blankInvoice = {
  invoiceId: '',
  invoiceDate: null,
  invoiceTotal: null,
  file: null,
};

export interface IInvoice {
  invoiceId: string;
  invoiceDate: Date | null;
  invoiceTotal: number | null;
  file: File | null;
}

const initialFormData = {
  invoices: [],
  isApproved: false,
  phone: {},
  type: '',
  fieldId: '',
};

interface ICannabisContext {
  showForm: boolean;
  showUploadFileArea: boolean;
  setShowUploadFileArea: React.Dispatch<React.SetStateAction<boolean>>;
  newInvoice: IInvoice;
  currentStep: number;
  formData: ICannabisFormData;
  setFormData: React.Dispatch<React.SetStateAction<ICannabisFormData>>;
  showDialog: boolean;
  dialogContent: JSX.Element;
  editedInvoiceIndex: number | null;
  isEligible: boolean;
  internetReqNum: number | null;
  isFileError: boolean;
  isLoading: boolean;
  loader: JSX.Element;
  handleNextStep: () => void;
  handlePrevStep: () => void;
  handleSubmit: () => Promise<void>;
  handleFile: (file: File) => Promise<void>;
  handleCloseModal: () => void;
  handleAddInvoice: (invoice: IInvoice) => void;
  deleteInvoice: (index: number) => void;
  watchFile: (fileData: File | null) => Promise<void>;
  checkIfInvoiceExists: (invoiceNumber: string) => boolean;
  editInvoice: (index: number) => void;
  onClickReturnToMain: () => void;
  onReturnFromEdit: (onClickSave: () => void) => void;
  onExit: (onClickSave: () => void) => void;
  getDialogOnClose: () => void;
  redirects: {
    text: string;
    href: string;
  }[];
  isOcrLoading: boolean;
  setIsOcrLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CannabisContext = createContext<ICannabisContext>({
  showForm: false,
  showUploadFileArea: false,
  setShowUploadFileArea: () => {},
  newInvoice: blankInvoice,
  currentStep: 1,
  formData: initialFormData,
  setFormData: () => {},
  showDialog: false,
  dialogContent: <></>,
  editedInvoiceIndex: null,
  isEligible: false,
  internetReqNum: null,
  isFileError: false,
  isLoading: false,
  loader: <></>,
  handleNextStep: () => {},
  handlePrevStep: () => {},
  handleSubmit: () => Promise.resolve(),
  handleFile: () => Promise.resolve(),
  handleCloseModal: () => {},
  handleAddInvoice: () => {},
  deleteInvoice: () => {},
  watchFile: () => Promise.resolve(),
  checkIfInvoiceExists: () => false,
  editInvoice: () => {},
  onClickReturnToMain: () => {},
  onReturnFromEdit: () => {},
  onExit: () => {},
  getDialogOnClose: () => {},
  redirects: [],
  isOcrLoading: true,
  setIsOcrLoading: () => {},
});

export const CannabisProvider: FC<{}> = (props) => {
  const [showForm, setShowForm] = useState(false);
  const [showUploadFileArea, setShowUploadFileArea] = useState(false);
  const [newInvoice, setNewInvoice] = useState<IInvoice>(blankInvoice);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ICannabisFormData>(initialFormData);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState<JSX.Element>(<></>);
  const [isFormReady, setIsFormReady] = useState(false);
  const [editedInvoiceIndex, setEditedInvoiceIndex] = useState<number | null>(
    null
  );
  const [isEligible, setIsEligible] = useState(false);
  const [internetReqNum, setInternetReqNum] = useState<number | null>(null);
  const [isFileError, setIsFileError] = useState(false);
  const { isLoading, loader, setIsLoading } = useLoader();
  const [isOcrLoading, setIsOcrLoading] = useState(false);

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  useEffect(() => {
    const checkEligibility = async () => {
      const response = await isCannabisRefundEntitled();
      setIsEligible(response || false);
      setIsLoading(false);
    };

    checkEligibility();
  }, [imposterId, setIsLoading]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchRequestIds = async () => {
      if (!isEligible) return;

      const res = await getReimbursements();
      if (!res) {
        navigate('/error');
        return;
      }
      const item = res?.find((item) => {
        return item.id === REIMBURSEMENTS_FORM_ID.Cannabis;
      });

      const type = item?.reqSubTypeId || '';
      setFormData((prevData) => ({ ...prevData, type }));

      const getFields = await getRequestFields(parseInt(type));
      if (!getFields) {
        navigate('/error');
        return;
      }
      const fieldId = getFields?.attachments[0].id || '';
      setFormData((prevData) => ({ ...prevData, fieldId }));
      setIsFormReady(true);
    };

    fetchRequestIds();
  }, [isEligible, navigate]);

  const personalDetails = usePersonalDetails();

  const setPhone = useCallback(() => {
    if (personalDetails) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        phone: {
          prefix: personalDetails.mainPhonePrefix,
          number: personalDetails.mainPhone,
        },
      }));
    }
  }, [personalDetails, setFormData]);

  useEffect(() => {
    setPhone();
    scrollToTop();
  }, [setPhone]);

  const handleNextStep = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    if (!isFormReady) {
      setIsLoading(false);
      return;
    }

    const prepareData = reimbursementsFormPrepareCannabisFormData(formData);
    const response = await createRequest(prepareData);
    setIsLoading(false);
    if (response && response.data?.internetReqNum) {
      setInternetReqNum(response.data.internetReqNum);
      setDialogContent(
        <SuccessDialog
          onClickFinish={() => {
            navigate('/');
          }}
          onClickAnotherRequest={() => {
            navigate('/reimbursements');
          }}
          numberOfInvoices={formData.invoices.length}
          requestNumber={response.data.internetReqNum}
          totalReimbursementSum={formData.invoices.reduce(
            (total, invoice) => total + (invoice.invoiceTotal || 0),
            0
          )}
        />
      );
      setShowDialog(true);
    } else if (
      parseInt((response as any)?.response?.status) !==
      HTTP_STATUS_CODES.Forbidden
    )
      navigate('/error');
  };

  const handleFile = async (file: File) => {
    if (checkFileSize(file) && checkExtension(file)) {
      if (!(await isFileValid(file))) {
        setIsFileError(true);
        return;
      }

      const duplicateFileIndex = formData.invoices.findIndex(
        (invoice: IInvoice) => invoice.file?.name === file.name
      );

      const fileData = new FormData();
      fileData.append('invoice', file, file.name);
      const currentInvoice =
        editedInvoiceIndex !== null
          ? formData.invoices[editedInvoiceIndex]
          : blankInvoice;
      setNewInvoice({ ...currentInvoice, file });
      setIsOcrLoading(true);
      const invoiceData = await analyzeInvoice(fileData);
      if (invoiceData.invoiceId) {
        invoiceData.invoiceId = invoiceData.invoiceId
          .replace(notLettersOrNumbersWithExtraChars, '')
          .slice(0, maxInvoiceLength);
      }
      setShowForm(false);

      if (duplicateFileIndex !== -1) {
        setIsOcrLoading(false);
        setDialogContent(
          <DuplicateFileDialog
            deleteInvoice={() => {
              setShowForm(false);
              setShowUploadFileArea(false);
              setShowDialog(false);
              setIsFileError(false);
            }}
            newInvoice={() => {
              setShowForm(false);
              setShowDialog(false);
            }}
            invoice={formData.invoices[duplicateFileIndex]}
          />
        );
        setShowDialog(true);
        return;
      }

      setNewInvoice(
        editedInvoiceIndex !== null
          ? { ...formData.invoices[editedInvoiceIndex] }
          : invoiceData
          ? { ...invoiceData, file }
          : { ...blankInvoice, file }
      );
      setShowForm(true);
      setIsFileError(false);
      setTimeout(() => {
        setIsOcrLoading(false);
      }, 0);
    } else {
      setIsFileError(true);
    }
  };

  const handleCloseModal = () => {
    setShowForm(false);
    setEditedInvoiceIndex(null);
    setNewInvoice(blankInvoice);
  };

  const handleAddInvoice = (invoice: IInvoice) => {
    let newInvoices = [...formData.invoices];

    if (editedInvoiceIndex === null) {
      newInvoices.push(invoice);
    } else {
      newInvoices[editedInvoiceIndex] = invoice;
    }

    setFormData((prevData) => ({ ...prevData, invoices: newInvoices }));
    setEditedInvoiceIndex(null);
    setShowForm(false);
    setShowUploadFileArea(false);
    setNewInvoice({
      ...blankInvoice,
    });
  };

  const deleteInvoice = (index: number) => {
    setDialogContent(
      <DeleteDialog
        invoice={formData.invoices[index]}
        closeModal={() => setShowDialog(false)}
        onClickYes={() => {
          const updatedInvoices = [...formData.invoices];
          updatedInvoices.splice(index, 1);
          setFormData((prevData) => ({
            ...prevData,
            invoices: updatedInvoices,
          }));
          setShowDialog(false);
        }}
      />
    );
    setShowDialog(true);
  };

  const watchFile = async (fileData: File | null) => {
    if (fileData) {
      window.open(URL.createObjectURL(fileData));
    } else {
      console.log('File not found');
    }
  };

  const checkIfInvoiceExists = (invoiceNumber: string) => {
    return (
      !editedInvoiceIndex &&
      !!formData.invoices.find(
        (invoice, index) =>
          index !== editedInvoiceIndex && invoice.invoiceId === invoiceNumber
      )
    );
  };

  const editInvoice = (index: number) => {
    setEditedInvoiceIndex(index);
    setNewInvoice(formData.invoices[index]);
    setShowForm(true);
  };

  const onClickReturnToMain = () => {
    setDialogContent(
      <ReturnToMainDialog
        onClickDelete={() => {
          navigate('/');
        }}
        onClickContinue={() => {
          setShowDialog(false);
        }}
      />
    );
    setShowDialog(true);
  };

  const onReturnFromEdit = (onClickSave: () => void) => {
    setDialogContent(
      <CancelEditDialog
        onClickGoBack={() => {
          setShowDialog(false);
          setShowForm(false);
        }}
        onClickSave={() => {
          onClickSave();
          setShowDialog(false);
        }}
      />
    );
    setShowDialog(true);
  };

  const onExit = (onClickDelete: () => void) => {
    if (editedInvoiceIndex !== null) {
      onClickDelete();
      return;
    }
    setDialogContent(
      <ReturnToMainDialog
        onClickContinue={() => setShowDialog(false)}
        onClickDelete={() => {
          onClickDelete();
          setShowDialog(false);
        }}
      />
    );
    setShowDialog(true);
  };

  const getDialogOnClose = () => {
    if (internetReqNum) {
      setInternetReqNum(null);
      navigate('/');
    } else {
      setShowDialog(false);
    }
  };

  useGeneralOptions(); // pre load general options

  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);
  const redirects = [
    {
      text: dictionary.noEntitle.cannabis.redirect1,
      href: '/universalRequest/2/2',
    },
    { text: dictionary.noEntitle.cannabis.redirect2, href: '/reimbursements' },
  ];

  return (
    <CannabisContext.Provider
      value={{
        showForm,
        showUploadFileArea,
        setShowUploadFileArea,
        newInvoice,
        currentStep,
        formData,
        setFormData,
        showDialog,
        dialogContent,
        editedInvoiceIndex,
        isEligible,
        internetReqNum,
        isFileError,
        isLoading,
        loader,
        handleNextStep,
        handlePrevStep,
        handleSubmit,
        handleFile,
        handleCloseModal,
        handleAddInvoice,
        deleteInvoice,
        watchFile,
        checkIfInvoiceExists,
        editInvoice,
        onClickReturnToMain,
        onReturnFromEdit,
        onExit,
        getDialogOnClose,
        redirects,
        isOcrLoading,
        setIsOcrLoading,
      }}
    >
      {props.children}
    </CannabisContext.Provider>
  );
};
